import type { DependencyChainNode, ScaVerdict, ScaVerdictMap } from './sca-dependency-tree.tsx'

type NodeIdentity = Pick<DependencyChainNode, 'name' | 'version'>

/**
 * Shared empty default for every `verdicts` prop. The tree's d3 effect depends on `verdicts`, so a
 * fresh `new Map()` per render would re-run it. Lives here because this module is d3-free.
 */
export const EMPTY_VERDICTS: ScaVerdictMap = new Map()

// When several chains share a TID, the TID-level verdict is the strongest of them. `inconclusive`
// outranks `not exploitable` because a TID cannot be ruled out while any of its chains is still
// open: the same fail-open rule analysis-service applies, which treats INCONCLUSIVE as reachable.
const VERDICT_PRECEDENCE: Record<ScaVerdict, number> = {
  exploitable: 3,
  inconclusive: 2,
  'not exploitable': 1,
  'not evaluated': 0,
}

export function strongestVerdict(a: ScaVerdict | undefined, b: ScaVerdict) {
  return a !== undefined && VERDICT_PRECEDENCE[a] >= VERDICT_PRECEDENCE[b] ? a : b
}

/** Verdicts that neither confirm nor rule out exploitability: their paths render as unconfirmed. */
export function isUnconfirmedVerdict(verdict: ScaVerdict | undefined) {
  return verdict === 'inconclusive' || verdict === 'not evaluated'
}

/** `name@version`, or the bare `name` when the version is unknown: the key report verdicts are written under. */
export function nodeKey({ name, version }: NodeIdentity) {
  return version ? `${name}@${version}` : name
}

/** `name@version -> name@version -> …`, the same form the report appendix uses. */
export function chainKey(chain: NodeIdentity[]) {
  return chain.map(nodeKey).join(' -> ')
}

/** Split one appendix entry (`a@1 -> b@2 -> …`, or a single `a@1`) into its packages. */
export function parseChainSegments(content: string): NodeIdentity[] {
  return content
    .trim()
    .split(/\s*->\s*/)
    .map(segment => {
      const atIdx = segment.lastIndexOf('@')
      return atIdx > 0
        ? { name: segment.slice(0, atIdx), version: segment.slice(atIdx + 1) }
        : { name: segment, version: undefined }
    })
}

/** The report's verdict for one package, keyed by `name@version` and falling back to the bare `name`. */
export function nodeVerdict(node: NodeIdentity, verdicts: ScaVerdictMap) {
  return verdicts.get(nodeKey(node)) ?? verdicts.get(node.name)
}

/** The report's verdict for a chain's TID: its second node, after the project root. */
export function tidVerdict(chain: NodeIdentity[], verdicts: ScaVerdictMap) {
  const tid = chain[1]
  return tid ? nodeVerdict(tid, verdicts) : undefined
}

/**
 * A chain's own verdict when the report labels chains individually, otherwise its TID's.
 * Older reports carry one verdict per TID, stamped on every chain under it.
 */
export function chainVerdict(chain: NodeIdentity[], verdicts: ScaVerdictMap) {
  return verdicts.get(chainKey(chain)) ?? tidVerdict(chain, verdicts)
}

/**
 * One representative path per TID: its shortest exploitable chain when it has one, else its
 * shortest chain, ties broken by report order. A TID whose subgraph fans out (one direct
 * dependency reaching the vulnerable package through many intermediate packages) otherwise
 * renders every path as its own branch.
 */
export function focusChains(chains: DependencyChainNode[][], verdicts: ScaVerdictMap) {
  const isExploitable = (chain: DependencyChainNode[]) => chainVerdict(chain, verdicts) === 'exploitable'
  const byTid = new Map<string, DependencyChainNode[]>()
  for (const chain of chains) {
    const tid = chain[1]
    const key = tid ? nodeKey(tid) : chainKey(chain)
    const current = byTid.get(key)
    const better =
      !current ||
      (isExploitable(chain) && !isExploitable(current)) ||
      (isExploitable(chain) === isExploitable(current) && chain.length < current.length)
    if (better) byTid.set(key, chain)
  }
  // Exploitable TIDs first so the highlighted path is laid out on the left.
  return [...byTid.values()].sort((a, b) => Number(isExploitable(b)) - Number(isExploitable(a)))
}
